import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../utils/api';

function formatAge(hours) {
  // Always display as days (1 decimal). Keeps "Waiting" column consistent
  // across the whole age range so assessors don't have to do hours-to-days
  // arithmetic in their head.
  const days = (hours / 24).toFixed(1);
  if (hours >= 96) return { text: `${days}d`, className: 'age-critical' };
  if (hours >= 72) return { text: `${days}d`, className: 'age-warning' };
  return { text: `${days}d`, className: 'age-ok' };
}

function statusBadge(status) {
  const map = {
    submitted: ['Pending', 'badge-pending'],
    ai_reviewed: ['AI Reviewed', 'badge-pending'],
    checked_out: ['Checked Out', 'badge-checked'],
    graded: ['Graded', 'badge-graded'],
    resubmit_requested: ['Resubmit', 'badge-overdue']
  };
  const [label, cls] = map[status] || [status, ''];
  return <span className={`badge ${cls}`}>{label}</span>;
}

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ submissions: [], counts: {} });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  // Filter & sort state
  const [courseFilter, setCourseFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('age_desc'); // age_desc, age_asc, student, course, assignment

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let path = '/submissions?';
      if (filter === 'pending') path += 'status=submitted&';
      else if (filter === 'ai_reviewed') path += 'status=ai_reviewed&';
      else if (filter === 'mine') path += 'mine=true&';
      else if (filter === 'overdue') path += 'status=submitted&';
      else if (filter === 'all') path += ''; // no status filter
      if (courseFilter) path += `course_id=${courseFilter}&`;
      const result = await get(path);
      let subs = result.submissions;
      if (filter === 'overdue') subs = subs.filter(s => s.hours_waiting >= 96);
      setData({ submissions: subs, counts: result.counts });
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, courseFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCheckout = async (id) => {
    try {
      await post(`/submissions/${id}/checkout`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRelease = async (id) => {
    try {
      await post(`/submissions/${id}/release`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  // Extract unique courses from submissions for the course dropdown
  const availableCourses = useMemo(() => {
    const courseMap = {};
    data.submissions.forEach(s => {
      if (s.course_name && s.course_id) {
        courseMap[s.course_id] = s.course_name;
      }
    });
    return Object.entries(courseMap).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data.submissions]);

  // Apply search and sort client-side
  const filteredAndSorted = useMemo(() => {
    let result = [...data.submissions];

    // Text search across student name, assignment name, course name
    if (searchText.trim()) {
      const term = searchText.toLowerCase().trim();
      result = result.filter(s =>
        (s.student_name || '').toLowerCase().includes(term) ||
        (s.assignment_name || '').toLowerCase().includes(term) ||
        (s.course_name || '').toLowerCase().includes(term) ||
        (s.student_email || '').toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortField) {
        case 'age_desc': return (b.hours_waiting || 0) - (a.hours_waiting || 0);
        case 'age_asc': return (a.hours_waiting || 0) - (b.hours_waiting || 0);
        case 'student': return (a.student_name || '').localeCompare(b.student_name || '');
        case 'course': return (a.course_name || '').localeCompare(b.course_name || '');
        case 'assignment': return (a.assignment_name || '').localeCompare(b.assignment_name || '');
        default: return 0;
      }
    });

    return result;
  }, [data.submissions, searchText, sortField]);

  const counts = data.counts || {};

  return (
    <div>
      <div className="stats-grid">
        <div className="card stat-card" onClick={() => setFilter('pending')} style={{cursor:'pointer'}}>
          <div className="stat-value">{counts.pending || 0}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="card stat-card" onClick={() => setFilter('mine')} style={{cursor:'pointer'}}>
          <div className="stat-value">{counts.checked_out || 0}</div>
          <div className="stat-label">Checked Out</div>
        </div>
        <div className="card stat-card" onClick={() => setFilter('overdue')} style={{cursor:'pointer'}}>
          <div className={`stat-value ${parseInt(counts.overdue) > 0 ? 'alert' : ''}`}>{counts.overdue || 0}</div>
          <div className="stat-label">Overdue (&gt;4d)</div>
        </div>
        <div className="card stat-card">
          <div className="stat-value">{counts.graded || 0}</div>
          <div className="stat-label">Graded</div>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <h2>Submissions Queue</h2>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        <div className="tabs">
          {[['pending','Pending'],['ai_reviewed','AI Reviewed'],['mine','My Checkouts'],['overdue','Overdue'],['all','All']].map(([key, label]) => (
            <button key={key} className={`tab ${filter===key?'active':''}`} onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </div>

        {/* Filter & Sort bar */}
        <div style={{
          display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center',
          padding:'12px 0', borderBottom:'1px solid #E5E7EB', marginBottom:'12px'
        }}>
          <div style={{flex:'1 1 200px', minWidth:'150px'}}>
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search student, assignment, or course..."
              style={{
                width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB',
                borderRadius:'6px', fontSize:'13px'
              }}
            />
          </div>
          <div style={{flex:'0 0 auto'}}>
            <select
              value={courseFilter}
              onChange={e => setCourseFilter(e.target.value)}
              style={{
                padding:'7px 10px', border:'1px solid #D1D5DB',
                borderRadius:'6px', fontSize:'13px', backgroundColor:'#fff',
                minWidth:'180px'
              }}
            >
              <option value="">All Courses</option>
              {availableCourses.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div style={{flex:'0 0 auto'}}>
            <select
              value={sortField}
              onChange={e => setSortField(e.target.value)}
              style={{
                padding:'7px 10px', border:'1px solid #D1D5DB',
                borderRadius:'6px', fontSize:'13px', backgroundColor:'#fff'
              }}
            >
              <option value="age_desc">Oldest First</option>
              <option value="age_asc">Newest First</option>
              <option value="student">Student Name (A-Z)</option>
              <option value="course">Course (A-Z)</option>
              <option value="assignment">Assignment (A-Z)</option>
            </select>
          </div>
          {(searchText || courseFilter) && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setSearchText(''); setCourseFilter(''); }}
              style={{fontSize:'12px'}}
            >
              Clear Filters
            </button>
          )}
          <span style={{fontSize:'12px', color:'#9CA3AF'}}>
            {filteredAndSorted.length} result{filteredAndSorted.length !== 1 ? 's' : ''}
          </span>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Assignment</th>
              <th>Course</th>
              <th>Waiting</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.length === 0 ? (
              <tr><td colSpan={6} style={{textAlign:'center', padding:'40px', color:'#9CA3AF'}}>
                {loading ? 'Loading...' : searchText ? 'No submissions match your search' : 'No submissions found'}
              </td></tr>
            ) : filteredAndSorted.map(s => {
              const age = formatAge(s.hours_waiting);
              return (
                <tr key={s.id}>
                  <td>
                    <strong style={{cursor:'pointer', color:'#00707F'}} onClick={() => navigate(`/submissions/${s.id}`)}>
                      {s.student_name}
                    </strong>
                  </td>
                  <td>
                    <span style={{cursor:'pointer', color:'#00707F'}} onClick={() => navigate(`/submissions/${s.id}`)}>
                      {s.assignment_name}
                    </span>
                    {!s.has_rubric && <span className="badge badge-overdue" style={{marginLeft:8,fontSize:10}}>No rubric</span>}
                    {s.ai_recommendation && (
                      <span className="badge badge-graded" style={{marginLeft:8,fontSize:10}}>AI: {s.ai_recommendation.replace(/_/g,' ')}</span>
                    )}
                  </td>
                  <td>{s.course_name}</td>
                  <td className={age.className}>{age.text}</td>
                  <td>{statusBadge(s.status)}</td>
                  <td>
                    {s.status === 'submitted' || s.status === 'ai_reviewed' ? (
                      <>
                        <button className="btn btn-primary btn-sm" onClick={() => handleCheckout(s.id)}
                                style={{marginRight:6}}>
                          Check Out
                        </button>
                        <a href={s.speedgrader_url} target="_blank" rel="noopener noreferrer"
                           className="btn btn-secondary btn-sm">
                          SpeedGrader
                        </a>
                      </>
                    ) : s.status === 'checked_out' && s.checked_out_by_name ? (
                      <>
                        <span style={{fontSize:13, color:'#6B7280', marginRight:8}}>
                          {s.checked_out_by_name}
                        </span>
                        {s.checked_out_by_name === user.name && (
                          <>
                            <a href={s.speedgrader_url} target="_blank" rel="noopener noreferrer"
                               className="btn btn-primary btn-sm" style={{marginRight:6}}>
                              Grade
                            </a>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleRelease(s.id)}>
                              Release
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <a href={s.speedgrader_url} target="_blank" rel="noopener noreferrer"
                         className="btn btn-secondary btn-sm">
                        View
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
